<template>
  <ul
    v-if="!!filter.options"
    class="filter-list"
    :class="[
      $blockClass(blockClass, 'filter-list'),
      {
        [$class('active')]: filtersActive,
        [$class('collapsed')]: !isExpanded,
      },
      `filter-list-${filter.key}`
    ]"
    tabindex="0"
    @mouseenter="handleMouseEnter"
    @mouseleave="handleMouseLeave"
  >
    <li
      v-for="option of filter.options"
      :key="`${filter.key}-${option.value}`"
      class="filter-item"
      data-gtm-event="filter-click"
      :class="[
        $blockClass(blockClass, 'filter-item'),
        {
          [$class('active')]: option.selected
        }
      ]"
    >
      <input
        :id="`${filter.key}-${option.value}`"
        :checked="option.selected"
        class="checkbox__input"
        type="checkbox"
        @change="(event) => handleFilterSelect(event, filter.key, option.value, filter.op)"
      >

      <label
        v-if="variant === 'primary'"
        class="checkbox__label"
        :htmlFor="`${filter.key}-${option.value}`"
        :style="filter.key == 'color_name' ?
          `--swatch-color: var(--theme-color-${option.value.trim().toLowerCase().replace(' ', '-')})
        ` : false"
        v-text="`${option.name}${showProductCount ? ` (${option.count})` : ''}`"
      />

      <label
        v-if="variant === 'secondary'"
        class="checkbox__label checkbox__label--secondary"
        :htmlFor="`${filter.key}-${option.value}`"
      >
        <span v-text="option.name" />
        <span
          v-if="showProductCount"
          v-text="option.count"
        />
      </label>
    </li>
  </ul>

  <button
    v-if="hiddenFilterCount > 0"
    type="button"
    class="filter-list__expand text-desktop-regular"
    @click="isExpanded = !isExpanded"
    v-text="isExpanded ? `- ${hiddenFilterCount} less` : `+ ${hiddenFilterCount} more`"
  />
</template>

<script>

/**
 * Vue: Filter list (filter-list)
 * -----------------------------------------------------------------------------
 * List of checkboxes with interactive hover states.
 * @param {String} [blockClass] - String indicating block level BEM class.
 * @param {Object} [filter] - Object containing filter key, label and
 * options.
 * @param {Boolean} [isSelected] - Boolean property determining if a filter
 * value is selected
 * @param {String} [variant] - String property to set list styling
 *
 */
import { mapState } from 'vuex'

export default {
  name: 'FilterList',

  props: {
    blockClass: {
      type: String,
      default: '',
    },

    filter: {
      type: Object,
      required: true,
      default: () => ({
        key: '',
        label: '',
        options: [],
      }),
    },

    isSelected: {
      type: Boolean,
      default: false,
    },

    variant: {
      type: String,
      default: 'primary',
      validator: (value) => {
        return ['', 'primary', 'secondary'].includes(value)
      },
    },
  },

  emits: {
    'filter-selected': (payload) => typeof payload === 'object',
  },

  data() {
    return {
      anyHover: false,
      isExpanded: false,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Returns whether to display the list active state.
     * @returns {Boolean}
     */
    filtersActive() {
      return this.isSelected
    },

    /**
     * Returns whether to show more than 6 items
     * @returns {Boolean}
     */
    filtersExpanded() {
      return this.isExpanded
    },

    /**
     * Show product count next to filters
     * @returns {Boolean}
     */
    showProductCount() {
      return cnvs.klevu.showProductCount || false
    },

    /**
     * Count of hidden filters
     * @returns {Number}
     */
    hiddenFilterCount() {
      return this.filter.options.length - 6
    },
  },

  methods: {

    /**
     * Handles mouse enter event.
     */
    handleMouseEnter() {
      if (this.isMobile) {
        return
      }

      this.anyHover = true
    },

    /**
     * Handles mouse leave event.
     */
    handleMouseLeave() {
      if (this.isMobile) {
        return
      }

      this.anyHover = false
    },

    /**
     * Handles link clicks.
     * @param {Object} event - Mouse input event.
     * @param {Object} key - Filter key.
     * @param {Object} value - Filter value.
     */
    handleFilterSelect(event, key, value, op) {
      this.$emit('filter-selected', { event, key, value, op })
      this.handleGtm(event, key, value)
    },

    /**
     * Utility function to reset component to default state - called by parent.
     */
    resetComponentData() {
      this.anyHover = false
      this.isExpanded = false
    },

    handleGtm(event, key, value) {
      const isActive = event.target.closest('[data-gtm-event="filter-click"]')?.classList.contains('is-active')

      window.dataLayer.push({
        event: 'filter_click',
        filterClick: {
          url: document.location.pathname,
          filter_type: key,
          filter_value: value,
          action: isActive ? 'remove' : 'add',
        },
      })
    },
  },
}
</script>

<style lang="scss">
@import './filter-list';
</style>
